import React, { useState, useEffect, useCallback } from "react";
import axios from "../services/httpClient";
import { toast } from "react-toastify";
import { FiUserPlus, FiMail, FiLock, FiUser } from "react-icons/fi";

const AdminCreateUser = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const [approvingUserId, setApprovingUserId] = useState(null);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.fullName || !formData.email || !formData.password) {
      toast.error("Please fill all the fields before submitting.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post("/v2/auth/signup-by-admin", {
        email: formData.email,
        password: formData.password,
        fullName: formData.fullName,
      });

      const successMessage =
        response?.data?.message || "User created successfully!";
      toast.success(successMessage);
      setFormData({
        fullName: "",
        email: "",
        password: "",
      });
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to create user. Please try again.";
      toast.error(errorMessage);
      console.error("Admin create user error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveUser = async (userId) => {
    if (!userId) return;
    setApprovingUserId(userId);
    try {
      const response = await axios.post("/v2/auth/approve-user", { userId });
      const message =
        response?.data?.message || "User approved successfully!";
      toast.success(message);
      fetchPendingUsers();
    } catch (error) {
      console.error("Failed to approve user:", error);
      const errorMessage =
        error?.response?.data?.message ||
        "Unable to approve user. Please try again.";
      toast.error(errorMessage);
    } finally {
      setApprovingUserId(null);
    }
  };

  const fetchPendingUsers = useCallback(async () => {
    setIsLoadingPending(true);
    try {
      const response = await axios.get("/v2/auth/pending-users");
      const users =
        response?.data?.users ??
        response?.data ??
        [];
      setPendingUsers(Array.isArray(users) ? users : []);
    } catch (error) {
      console.error("Failed to fetch pending users:", error);
      toast.error("Unable to load pending users. Please try again.");
      setPendingUsers([]);
    } finally {
      setIsLoadingPending(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingUsers();
  }, [fetchPendingUsers]);

  const formatDate = (value) => {
    if (!value) return "—";
    try {
      return new Date(value).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (err) {
      return value;
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 md:p-10 space-y-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shadow-inner">
                <FiUserPlus className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">
                  User Management
                </h1>
                <p className="text-gray-500">
                  Create new students and approve pending accounts from a single
                  place.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowCreateForm((prev) => !prev)}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-primary text-white font-semibold shadow-md shadow-primary/30 hover:shadow-lg hover:shadow-primary/40 transition-all"
            >
              <FiUserPlus className="w-5 h-5" />
              {showCreateForm ? "Hide Form" : "Create User"}
            </button>
          </div>

          {showCreateForm && (
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <FiUser className="w-4 h-4 text-primary" />
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Enter full name"
                    className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <FiMail className="w-4 h-4 text-primary" />
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="student@example.com"
                    className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <FiLock className="w-4 h-4 text-primary" />
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter a secure password"
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Password must be at least 8 characters long.
                </p>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-primary text-white font-semibold shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin rounded-full h-5 w-5 border-t-2 border-white border-opacity-70" />
                      Creating user...
                    </>
                  ) : (
                    <>
                      <FiUserPlus className="w-5 h-5" />
                      Create User
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 md:p-10 space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Pending Admin Approved Users
              </h2>
              <p className="text-gray-500">
                Review and approve users awaiting manual verification.
              </p>
            </div>
            <button
              type="button"
              onClick={fetchPendingUsers}
              className="inline-flex items-center justify-center px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Refresh List
            </button>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-gray-100">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Requested On
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {isLoadingPending ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center">
                      <div className="flex flex-col items-center gap-3 text-gray-500">
                        <span className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary border-opacity-70" />
                        Loading pending users...
                      </div>
                    </td>
                  </tr>
                ) : pendingUsers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-gray-500">
                      No pending users at the moment.
                    </td>
                  </tr>
                ) : (
                  pendingUsers.map((user) => {
                    const userId = user.userId || user.id || user._id;
                    const fullName = user.fullName || user.name || "—";
                    const email = user.email || "—";
                    const createdAt = user.createdAt || user.requestedAt || null;

                    return (
                      <tr key={userId} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900">
                          {fullName}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">{email}</td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {formatDate(createdAt)}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => handleApproveUser(userId)}
                            disabled={approvingUserId === userId}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-2xl bg-emerald-500 text-white shadow-md hover:bg-emerald-600 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                          >
                            {approvingUserId === userId ? "Approving..." : "Approve"}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCreateUser;

